<template>
    <date-picker
            class="date-input"
            :style="menuWidth"
            :model-value="pickedDate"
            :inline="inline"
            :multi-dates="multiDates"
            :disabled="disabled"
            :min-date="minDate"
            :max-date="maxDate"
            :disabled-dates="disabledDates"
            :highlight="highlight"
            :start-date="startDate"
            :formats="formats"
            :time-config="TIME_PICKER_OFF"
            :input-attrs="inputAttrs"
            :aria-labels="ariaLabels"
            :ui="ui"
            :week-start="1"
            :auto-apply="true"
            :hide-offset-dates="true"
            @update:model-value="onPicked"
            @update-month-year="$emit('update-month-year', $event)"
    >
        <!-- ours rather than the library's, which arrives unstyled under compat. The
             library wraps whatever this slot holds in the span that positions it -->
        <template #input-icon>
            <i class="fal fa-calendar-alt date-input__icon" aria-hidden="true"></i>
        </template>
    </date-picker>
</template>

<script>
    /**
     * The app's date field, over @vuepic/vue-datepicker.
     *
     * The sixteen pickers in the app all asked the library for the same things - a date and
     * no time, applied on the click rather than through an action row, formatted DD/MM/YYYY,
     * a calendar to open - so those live here rather than being spelled out again at every
     * call site. What a call site still says is what differs between them: the bounds, which
     * dates are out, and whether it is an inline calendar.
     *
     * Replaces vuejs-datepicker, whose props this deliberately does not imitate:
     *
     *   `:disabled-dates="{to, from, dates}"` -> `:min-date` / `:max-date` / `:disabled-dates`
     *      the old `to` and `from` were exclusive, so the bound itself stayed pickable, which
     *      is what min-date and max-date mean. They compare by day, where the old ones
     *      compared the full Date - a bound of `new Date()` used to take today out with it
     *   `:highlighted="{dates: [today]}"` -> nothing
     *      today is marked by the library, so the fourteen pickers that highlighted it by
     *      hand say nothing at all now. `highlighted-dates` is left for the two that mark
     *      something else - the days a diary has entries on, the occurrences of an event
     *   `#afterDateInput` + `showCalendar()` -> nothing
     *      the input has its own calendar button, so the input-group the call sites built
     *      around it, and the refs reaching in to open it, are gone
     *
     * Week starts on Monday, as it does in the uiv `<date-picker>` the event popups use.
     *
     * ## The two icons, and why they are handled here
     *
     * Vue 3 does not pass a fallthrough class to a *functional* component written as a
     * plain function - an options object gets one, a function does not - and every icon this
     * library ships is a plain function. Two of them are handed a class rather than being styled by a
     * descendant rule, so those two arrive with nothing on them: unpositioned, and at the
     * size an svg with no width is, which is enormous.
     *
     * The calendar icon is the app's own now, through `#input-icon`: the library wraps
     * whatever that slot holds in the span that positions it, so the class it would have
     * dropped is on a real element instead - and a font icon is what the input-group beside
     * these fields used to hold anyway. The clear icon keeps the library's button, which is
     * focusable where a slot would leave a span, and gets the sizing put back in the style
     * block below. The chevrons are fine: they are styled by a rule on their parent.
     */
    import moment from 'moment';
    import DatePicker from '~/vue/wrapped_libraries/Datepicker';

    const VISUAL_DATE_FORMAT = 'DD/MM/YYYY';

    // dates only. In v14 the time picker is switched off through `time-config`, and the
    // input's own settings - the clear button, the calendar button - through `input-attrs`
    const TIME_PICKER_OFF = { enableTimePicker: false };

    export default {
        name: 'DateInput',
        components: { DatePicker },
        props: {
            // a Date, or the array of them in `multi-dates`. A caller holding '' or
            // undefined before anything is picked is the same as holding nothing
            modelValue: { type: [Date, Array, String], default: null },
            // both inclusive: the day named is still pickable
            minDate: { type: Date, default: undefined },
            maxDate: { type: Date, default: undefined },
            // individual days out, where the bounds are not the shape of it
            disabledDates: { type: Array, default: undefined },
            // days to mark. Today is marked without being asked
            highlightedDates: { type: Array, default: undefined },
            // a calendar drawn in the page rather than a field that opens one
            inline: { type: Boolean, default: false },
            // clicking a day adds or removes it, and the model is an array of Dates
            multiDates: { type: Boolean, default: false },
            disabled: { type: Boolean, default: false },
            // the month to open on, when it is not the month of the value
            startDate: { type: Date, default: undefined },
            // an x in the field, for callers whose date is optional
            clearable: { type: Boolean, default: true },
            // what the field is called, for a screen reader: the call sites label these with
            // a `<span>` beside the input rather than a `<label for>`
            label: { type: String, default: undefined },
            inputClass: { type: [String, Array], default: undefined },
            // the calendar's minimum width, for a caller fitting one to a narrow screen
            width: { type: Number, default: undefined },
            format: { type: Function, default: date => moment(date).format(VISUAL_DATE_FORMAT) },
        },
        emits: ['update:modelValue', 'update-month-year'],
        computed: {
            TIME_PICKER_OFF () {
                return TIME_PICKER_OFF;
            },
            pickedDate () {
                return this.modelValue || null;
            },
            formats () {
                return { input: this.format };
            },
            highlight () {
                if (!this.highlightedDates) {
                    return;
                }
                return {
                    dates: this.highlightedDates,
                    // a highlighted day that is also out of bounds still shows as marked,
                    // which is what the diary calendar wants of a day it cannot open
                    options: { highlightDisabled: true },
                };
            },
            inputAttrs () {
                return { clearable: this.clearable };
            },
            ariaLabels () {
                return this.label ? { input: this.label } : undefined;
            },
            // only the keys there is a class for: the library reads every key it is handed
            // as a class name, so an `input: undefined` is a render error rather than a
            // field with no extra class on it
            ui () {
                return this.inputClass ? { input: this.inputClass } : {};
            },
            menuWidth () {
                return this.width ? { '--dp-menu-min-width': `${this.width}px` } : undefined;
            },
        },
        methods: {
            /**
             * The day, with no time left on it.
             *
             * The library hands back the day that was clicked carrying the time it was
             * clicked at, where vuejs-datepicker's dates were midnight. Every caller here
             * formats or compares whole days, and a stray time travels: `startDate > new
             * Date()` starts answering differently through the afternoon, and the diary's
             * `toISOString().substr(0, 10)` reads a different day either side of midnight.
             */
            startOfDay (date) {
                return moment(date).startOf('day').toDate();
            },
            onPicked (value) {
                if (Array.isArray(value)) {
                    this.$emit('update:modelValue', value.map(date => this.startOfDay(date)));
                    return;
                }
                this.$emit('update:modelValue', value ? this.startOfDay(value) : null);
            },
        },
    };
</script>

<style scoped lang="scss">
  @use '../../angular/app/styles/const' as *;

  // the library draws from css variables, so bootstrap 3's field is a handful of them
  // rather than a rewrite of its stylesheet
  .date-input {
    --dp-font-family: inherit;
    --dp-font-size: #{$default-font-size};
    --dp-text-color: #{$black};
    --dp-border-color: #{$gray};
    --dp-border-radius: 4px;
    // the ring on today and the fill on the picked day
    --dp-primary-color: #{$blue};
    --dp-highlight-color: #cae5ed;
  }

  // `dp--flex-display` is the library's mark for an inline calendar: the root turns into a
  // flex container, and its one child is content width, so the calendar sits against the
  // left edge of whatever box the caller drew it in until it is told otherwise
  .date-input.dp--flex-display {
    justify-content: center;
  }

  // sat in the padding `dp--input-icon-pad` leaves at the start of the field. The library's
  // own icon carried this spacing in the class it also uses to size itself, which a font
  // icon has no need of
  .date-input__icon {
    padding: 6px 12px;
    line-height: $default-font-size;
  }

  // the sizing the clear icon lost with its class. Its button is positioned by one of its
  // own, so this is only what `dp--input-icons` would have set on the svg inside it
  .date-input:deep(.dp--clear-btn svg) {
    box-sizing: content-box;
    width: var(--dp-font-size);
    height: var(--dp-font-size);
    padding: 6px 12px;
  }
</style>
